import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { easyPayCalculate } from "../../../../../utility/features/helpers/accountHelper";
import ViewPaymentMethodsClient from "../../../../../utility/apiClients/customer/ViewPaymentMethodsClient";
import OverLayLoader from "../../../../loader/overlayLoader";
import Loader from "../../../../loader";
import tempJson from "../../../../../json/fallbackglobalvariables.json";
import Globals from "../../../../../utility/constants/appConstants";
import { customerServiceNumberFormatted } from '../../../../../utility/utils/formatter';
import { PrepayNewPaymentMethodV2, PrepayExistingPaymentMethodV2 } from "../../../../../utility/apiClients/account/prePayOTPaymentClient";
import PaymentBlock from "../../../accountDetailsSharedBlocks/PaymentBlock";
import SelectionBlock from "../../../accountDetailsSharedBlocks/SelectionBlock";
import {setBypassAccountDetailsCache} from "../../../../../reduxStore/myAccount/accountSummarySlice";
import customerSummaryClient from "../../../../../utility/apiClients/customer/customerSummaryClient";
import { setCustomerInfo } from "../../../../../reduxStore/login/loginSlice";
import ErrorBlock from '../../../../errorMessage';
import missionStatusClient from '../../../../../utility/apiClients/user/missionStatusClient';

const Preferences = () => {
  const selectedInvoiceRef = useRef(null);
  const dispatch = useDispatch();
  const nav = useNavigate();
  const { customerDetails } = useSelector((state) => state.getCustomerDetails || {});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setErrorMsg] = useState("");
  const [paymentData, setPaymentData] = useState([]);
  const customerSummary = useSelector((state) => state.login.customerInfo);
  const labels = useMemo(() => tempJson.acc, []);

  const missionStatusCheck = async () => {
    try {
      const missionStatus = await missionStatusClient();
      return !["yes"].includes(String(missionStatus?.data).trim().toLowerCase());
    } catch (err) {
      console.error("Error checking mission status api:", err);
      return false;
    }
  };

  const [hasSelection, setHasSelection] = useState(false);
  const salesAgreementData = customerDetails?.SalesAgreements || [];
  const [isPaymentSubmitted, setIsPaymentSubmitted] = useState(false);
  
  const { easyPaySvcAg = [], nonEasyPaySvcAg = [] } = useMemo(() => {
    const salesAgreements = customerDetails?.SalesAgreements;

    if (!Array.isArray(salesAgreements) || salesAgreements.length === 0) {
      return { easyPaySvcAg: [], nonEasyPaySvcAg: [] };
    }

    const result = easyPayCalculate(salesAgreements) || {};
    return {
      easyPaySvcAg: result.easyPaySvcAg ?? [],
      nonEasyPaySvcAg: result.nonEasyPaySvcAg ?? [],
    };
  }, [customerDetails]);

  const selectedServiceAddress = useSelector((state) => state.accountSummary.selectedServiceAddress);
  const custNumbers = JSON.parse(localStorage.getItem("customerNumbers")) || [];

  const [serviceAddress, customerNumber] = useMemo(() => {
      let custNumber = "";
      let serviceAddr = "";
      if(selectedServiceAddress != null){
        [serviceAddr, custNumber] = selectedServiceAddress.split(" | ");
      } else {
        custNumber = customerSummary?customerSummary[0].CustomerNumber:"";
        serviceAddr = customerSummary?customerSummary[0].ServiceAddresses[0].address:"";
      }
  
      return [serviceAddr, custNumber];
    }, [selectedServiceAddress, customerSummary]);
  
  const makeSalesAgreementObj = (salesAgreements) => {
    let value = [];
    let data = {};
    salesAgreements.forEach((sa) => {
      if(selectedInvoiceRef.current !== null && selectedInvoiceRef.current?.[sa.salesAgrNum] === false)
        return;
      
      if(selectedInvoiceRef.current !== null && !Object.keys(selectedInvoiceRef.current).includes(sa.salesAgrNum))
        return;        

      value.push(sa.salesAgrNum);
      data = {...data, [sa.salesAgrNum]: sa };
    });

    return { value, data };
  }

  const getCustomerSummary = async (custNum) => {
    setErrorMsg("");
    if (custNum === "") return;

    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      return;
    }
   
    try {
      const customerSummary = await customerSummaryClient(custNum);

      if (customerSummary?.status && customerSummary?.status === 401 ) {
        console.log("Unauthorized access - 401");
        localStorage.clear();
        nav("/login", { replace: true });
        return;
      }
      
      if (customerSummary?.data === null || customerSummary?.data === undefined) {
        setErrorMsg("Some error occurred.");
        return;
      }
      dispatch(setCustomerInfo(customerSummary?.data));
    } catch (err) {
      setErrorMsg("Some error occurred.");
    } finally {
  
    }
  };

  const handleDataSubmit = async (data, isNew) => {
    setIsLoading(true);
    setErrorMsg("");
    setIsPaymentSubmitted(true);

    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      setIsLoading(false);
      window.scrollTo(0, 0);
      return;
    }

    const salesAgreementObj = makeSalesAgreementObj(salesAgreementData);
    
    if (isNew) {
      try {
        const response = await PrepayNewPaymentMethodV2(
          isNew,
          "easypay",
          data,
          customerNumber,
          salesAgreementObj,
          ""
        );

        if(typeof response.error === "undefined"){
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "account_action",
            category: "Account",
            action: "payments",
            label: `prepay initiated - ${
              data.isCreditCard ? "Card" : "Account"
            }`,
            customer_id: customerNumber || {},
            error: {
              reason: "",
            },
          });

          dispatch(setBypassAccountDetailsCache(true));

          let checkedData = sessionStorage.getItem("paymentlist");
          let parseList = JSON.parse(checkedData);
          if (data.isCreditCard && !data.isAccount) {
            parseList[0].successData = {
              method: data.cashSubType,
              confirmation: response,
              data: {
                pccAcctLast4: data["Card Number"].slice(-4),
              },
            };
          } else {
            parseList[0].successData = {
              method: "CHK",
              confirmation: response,
              data: {
                pccAcctLast4: data.tokenNum.slice(-4),
              },
            };
          }

          sessionStorage.setItem("paymentlist", JSON.stringify(parseList));

          return response;
        } else {
            setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
            window.scrollTo(0, 0);
        }
      } catch (error) {
        if(error?.genericMessage)
          setErrorMsg(error?.genericMessage);
        else
          setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
        window.scrollTo(0, 0);
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const response = await PrepayExistingPaymentMethodV2(
          isNew,
          "easypay",
          data,
          customerNumber,
          salesAgreementObj,
          ""
        );

        if (response && response.status === 500) {
          throw response.error || "Internal Server Error";
        }

        if(typeof response.error === "undefined"){

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "account_action",
              category: "Account",
              action: "payments",
              label: `enrollment initiated - ${paymentData.pccCashType === 'CCD' ? 'Card' : 'Account'}`,
              customer_id: customerNumber || {},
              error: {
                reason: "",
              },
            });

            dispatch(setBypassAccountDetailsCache(true));

            let checkedData = sessionStorage.getItem("paymentlist");
            let parseList = JSON.parse(checkedData);
            if (data.pccCashType!=="CHK") {
              parseList[0].successData = {
                method: data.pccCashSubType,
                confirmation: response,
                data: {
                  pccAcctLast4: data.pccTokenNum.slice(-4),
                },
              };
            } else {
              parseList[0].successData = {
                method: "CHK",
                confirmation: response,
                data: {
                  pccAcctLast4: data.pccTokenNum.slice(-4),
                },
              };
            }

            sessionStorage.setItem("paymentlist", JSON.stringify(parseList));

            return response;
        } else {
          setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
          window.scrollTo(0, 0);
        }
      } catch (error) {
        setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
        window.scrollTo(0, 0);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchPaymentData = async () => {
      const isMissionAvailable = await missionStatusCheck();
      if (!isMissionAvailable) {
        setErrorMsg(Globals.acc.missionDownErrorDisplay);
        setIsLoading(false);
        return;
      }

      try {
        const paymentInfo = await ViewPaymentMethodsClient({
          cusNum: customerNumber ?? custNumbers?.[0],
        });

        const methods = paymentInfo?.data?.PaymentMethods || [];
        setPaymentData(methods);
        setIsLoading(false);
      } catch (error) {
        setErrorMsg("Some error occurred.");
        console.error("Error fetching payment methods:", error);
      }
  };
  
  useEffect(() => {
    setErrorMsg("");
    setIsLoading(true);
    setPaymentData([]);
    
    fetchPaymentData();

    if (customerNumber != "") {
      (async () => {
        await getCustomerSummary(customerNumber);
      })();
    }
  }, [customerNumber, selectedServiceAddress]);
  
  return (
    <>
      {error != "" && <ErrorBlock labels={labels} errMsg={error} />}
      {error == "" && isLoading && <Loader />}
      <div>
        

        <div>
          {error == "" && !isLoading && !!paymentData.length && (
            <>
              <SelectionBlock
                paymentData={{ easyPaySvcAg, nonEasyPaySvcAg }}
                setHasSelection={setHasSelection}
                ref={selectedInvoiceRef}
                isPaymentSubmitted={isPaymentSubmitted}
              />

              {hasSelection &&
              <PaymentBlock
                paymentDataProp={paymentData}
                handleDataSubmit={handleDataSubmit}
                hasSelection={hasSelection}
                flag={"easyPayADDChange"}
              />}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Preferences;
